import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contractsAPI, inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', EUR: '€' };

export default function PurchaseOrderFormPage() {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState('Finished Good');

  const [form, setForm] = useState({
    customer_id: '', po_number: '', po_date: '', project_number: '',
    value: '', description: '', remarks: '', email_recipients: '',
    currency: 'INR', conversion_factor: '1', delivery_due_date: '',
  });
  const [poItems, setPOItems] = useState([{ item_id: '', quantity: '', unit_price: '', description: '', due_date: '' }]);

  // Conversion-factor dialog (opens when a non-INR currency is picked)
  const [convDialogOpen, setConvDialogOpen] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState('INR');
  const [convFactorInput, setConvFactorInput] = useState('');

  const curSymbol = CURRENCY_SYMBOLS[form.currency] || '₹';

  useEffect(() => {
    (async () => {
      try {
        const [custRes, itemsRes] = await Promise.all([
          contractsAPI.activeCustomers(),
          inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        ]);
        setCustomers(custRes.data || []);
        setItems(itemsRes.data.items || []);
      } catch { toast.error('Failed to load data'); }
    })();
  }, []);

  const addItem = () => setPOItems(prev => [...prev, { item_id: '', quantity: '', unit_price: '', description: '', due_date: '' }]);
  const removeItem = (idx) => { if (poItems.length > 1) setPOItems(prev => prev.filter((_, i) => i !== idx)); };
  const updateItem = (idx, field, value) => setPOItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.po_number || !form.po_date) {
      toast.error('Customer, PO number, and date are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        currency: form.currency || 'INR',
        conversion_factor: form.currency === 'INR' ? 1 : (parseFloat(form.conversion_factor) || 1),
        value: form.value ? parseFloat(form.value) : 0,
        items: poItems.filter(pi => pi.item_id && pi.quantity).map(pi => ({
          item_id: pi.item_id,
          quantity: parseFloat(pi.quantity),
          unit_price: pi.unit_price ? parseFloat(pi.unit_price) : 0,
          description: pi.description,
          due_date: pi.due_date || '',
        })),
      };
      const res = await contractsAPI.createPurchaseOrder(payload);
      toast.success('Purchase order created');
      navigate(`/contracts/purchase-orders/${res.data.id}`);
    } catch (err) {
      // Surface the underlying cause even when the backend returns Pydantic
      // (422) errors (where `detail` is an array of objects) OR when the
      // request never reaches the server (network failure / CORS). Without
      // this, toast.error([{...}, {...}]) renders nothing and the user sees
      // a silent failure on Create.
      // eslint-disable-next-line no-console
      console.error('Create PO failed:', err);
      const data = err?.response?.data;
      let msg = 'Failed to create PO';
      if (!err?.response) {
        msg = 'Network error — request did not reach the server. Check connection / VPN / CORS.';
      } else if (Array.isArray(data?.detail)) {
        msg = data.detail
          .map(e => {
            const loc = Array.isArray(e?.loc) ? e.loc.slice(1).join('.') : '';
            return loc ? `${loc}: ${e?.msg || 'invalid'}` : (e?.msg || 'invalid');
          })
          .join('; ');
      } else if (typeof data?.detail === 'string') {
        msg = data.detail;
      } else if (typeof data === 'string') {
        msg = data;
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(msg);
    }
    finally { setLoading(false); }
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Currency dropdown: INR resets factor to 1; USD/EUR opens the factor dialog.
  const handleCurrencyChange = (cur) => {
    if (cur === 'INR') {
      setForm(prev => ({ ...prev, currency: 'INR', conversion_factor: '1' }));
      return;
    }
    setPendingCurrency(cur);
    setConvFactorInput(form.currency === cur ? form.conversion_factor : '');
    setConvDialogOpen(true);
  };

  const confirmConversionFactor = () => {
    const f = parseFloat(convFactorInput);
    if (!f || f <= 0) { toast.error('Enter a valid conversion factor (greater than 0)'); return; }
    setForm(prev => ({ ...prev, currency: pendingCurrency, conversion_factor: convFactorInput }));
    setConvDialogOpen(false);
  };


  // Auto-calculate PO value from item totals
  const calculatedTotal = poItems.reduce((sum, pi) => {
    const qty = parseFloat(pi.quantity) || 0;
    const price = parseFloat(pi.unit_price) || 0;
    return sum + (qty * price);
  }, 0);

  // Sync PO value whenever items change
  useEffect(() => {
    if (calculatedTotal > 0) {
      setForm(prev => ({ ...prev, value: calculatedTotal.toString() }));
    }
  }, [calculatedTotal]);

  const customerOptions = customers.map(c => ({
    value: c.id, label: c.customer_name, sub: c.customer_code,
    searchStr: `${c.customer_name} ${c.customer_code} ${c.email || ''}`,
  }));

  const filteredItems = itemTypeFilter === 'All' ? items : items.filter(i => i.item_type === itemTypeFilter);
  const itemOptions = filteredItems.map(i => ({
    value: i.id, label: `${i.unique_id} - ${i.item_name}`, sub: i.item_type,
    searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
  }));
  const itemTypeOptions = ['Finished Good', 'Semi-Finished Part', 'Raw Material', 'Job Work Item', 'Consumable', 'Fixed Asset', 'Service', 'Scrap', 'All'];

  return (
    <div className="space-y-4 max-w-4xl" data-testid="po-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="font-heading text-xl font-bold tracking-tight">Register Purchase Order</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">PO Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Customer *</Label>
              <SearchableSelect
                options={customerOptions}
                value={form.customer_id}
                onSelect={v => u('customer_id', v)}
                placeholder="Search customers..."
                emptyText="No active customers"
                testId="po-customer-select"
              />
            </div>
            <div>
              <Label className="text-xs">PO Number *</Label>
              <Input value={form.po_number} onChange={e => u('po_number', e.target.value)}
                placeholder="e.g. PO-2026-001" className="h-8 text-xs font-mono" data-testid="po-number-input" />
            </div>
            <div>
              <Label className="text-xs">PO Date *</Label>
              <Input type="date" value={form.po_date} onChange={e => u('po_date', e.target.value)}
                className="h-8 text-xs" data-testid="po-date-input" />
            </div>
            <div>
              <Label className="text-xs">Project Number</Label>
              <Input value={form.project_number} onChange={e => u('project_number', e.target.value)}
                className="h-8 text-xs" data-testid="po-project" />
            </div>
            <div>
              <Label className="text-xs">Delivery Due Date</Label>
              <Input type="date" value={form.delivery_due_date} onChange={e => u('delivery_due_date', e.target.value)}
                className="h-8 text-xs" data-testid="po-delivery-due-date" />
            </div>
            <div>
              <Label className="text-xs">PO Currency</Label>
              <select
                value={form.currency}
                onChange={e => handleCurrencyChange(e.target.value)}
                className="h-8 text-xs border rounded px-2 bg-white w-full"
                data-testid="po-currency-select"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
              {form.currency !== 'INR' && (
                <p className="text-[10px] text-emerald-700 mt-1" data-testid="po-conversion-note">
                  1 {form.currency} = ₹{form.conversion_factor} ·{' '}
                  <button type="button" className="underline" onClick={() => handleCurrencyChange(form.currency)} data-testid="po-edit-factor-btn">edit</button>
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">PO Value ({curSymbol}) {calculatedTotal > 0 && <span className="text-[10px] text-green-600 font-normal ml-1">Auto-calculated</span>}</Label>
              <Input type="number" value={form.value} onChange={e => u('value', e.target.value)}
                className="h-8 text-xs font-medium" data-testid="po-value" readOnly={calculatedTotal > 0} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={e => u('description', e.target.value)}
                className="text-xs min-h-[40px]" data-testid="po-description" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Items</CardTitle>
              <div className="flex items-center gap-2">
                <select
                  value={itemTypeFilter}
                  onChange={e => setItemTypeFilter(e.target.value)}
                  className="h-6 text-[10px] border rounded px-1.5 bg-white"
                  data-testid="po-item-type-filter"
                >
                  {itemTypeOptions.map(t => (
                    <option key={t} value={t}>{t} ({t === 'All' ? items.length : items.filter(i => i.item_type === t).length})</option>
                  ))}
                </select>
                <Button type="button" size="sm" variant="outline" onClick={addItem} className="h-6 text-[10px]" data-testid="add-po-item-btn">
                  <Plus className="w-3 h-3 mr-0.5" /> Add Item
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {poItems.map((pi, idx) => (
              <Card key={idx} className="p-3 bg-muted/30">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Item</Label>
                      <SearchableSelect
                        options={itemOptions}
                        value={pi.item_id}
                        onSelect={v => updateItem(idx, 'item_id', v)}
                        placeholder="Search items..."
                        emptyText="No items."
                        testId={`po-item-${idx}`}
                      />
                    </div>
                    {poItems.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 mt-4 text-red-500" onClick={() => removeItem(idx)} data-testid={`remove-po-item-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Qty *</Label>
                      <Input type="number" value={pi.quantity}
                        onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        className="h-7 text-xs" data-testid={`po-item-qty-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Unit Price ({curSymbol})</Label>
                      <Input type="number" value={pi.unit_price}
                        onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        className="h-7 text-xs" data-testid={`po-item-price-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Line Total</Label>
                      <div className="h-7 flex items-center text-xs font-medium text-slate-700 bg-slate-50 border rounded px-2" data-testid={`po-item-total-${idx}`}>
                        {((parseFloat(pi.quantity) || 0) * (parseFloat(pi.unit_price) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Description</Label>
                      <Input value={pi.description}
                        onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="h-7 text-xs" data-testid={`po-item-desc-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Due Date</Label>
                      <Input type="date" value={pi.due_date || ''}
                        onChange={e => updateItem(idx, 'due_date', e.target.value)}
                        className="h-7 text-xs" data-testid={`po-item-due-date-${idx}`} />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </CardContent>
          {calculatedTotal > 0 && (
            <div className="px-6 pb-4 flex justify-end">
              <div className="text-right bg-violet-50 border border-violet-200 rounded-lg px-4 py-2" data-testid="po-items-grand-total">
                <span className="text-[10px] text-violet-600 font-medium block">Grand Total</span>
                <span className="text-sm font-bold text-violet-800">{curSymbol} {calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                {form.currency !== 'INR' && (
                  <span className="text-[10px] text-violet-500 block mt-0.5" data-testid="po-grand-total-inr">
                    ≈ ₹{(calculatedTotal * (parseFloat(form.conversion_factor) || 1)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (internal)
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>

        <Card>
          <CardContent className="pt-4 space-y-3">
            <div>
              <Label className="text-xs">Email Recipients</Label>
              <Input value={form.email_recipients} onChange={e => u('email_recipients', e.target.value)}
                placeholder="Comma-separated emails for PO acknowledgment (leave blank to use customer default)"
                className="h-8 text-xs" data-testid="po-email-recipients" />
              <p className="text-[10px] text-muted-foreground mt-1">Optional. Used when sending PO acknowledgment emails instead of the customer master email.</p>
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={form.remarks} onChange={e => u('remarks', e.target.value)}
                className="text-xs min-h-[40px]" data-testid="po-remarks" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-po-btn">
            <Save className="w-4 h-4 mr-1" /> {loading ? 'Saving...' : 'Create PO'}
          </Button>
        </div>
      </form>

      <Dialog open={convDialogOpen} onOpenChange={setConvDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="conversion-factor-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Conversion factor for {pendingCurrency}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">1 {pendingCurrency} = ? INR</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">1 {pendingCurrency} =</span>
              <Input
                type="number"
                value={convFactorInput}
                onChange={e => setConvFactorInput(e.target.value)}
                placeholder="e.g. 90"
                className="h-8 text-xs w-32"
                autoFocus
                data-testid="conversion-factor-input"
              />
              <span className="text-sm font-medium">INR</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              All prices are entered in {pendingCurrency}; the system stores the INR value (entered × factor) for internal calculations.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConvDialogOpen(false)} data-testid="conversion-cancel-btn">Cancel</Button>
            <Button type="button" onClick={confirmConversionFactor} data-testid="conversion-confirm-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
